import React, { ChangeEvent } from "react";
import { Component } from "react";
import { Form, Button } from "semantic-ui-react";
import { ReactCookieProps, withCookies } from "react-cookie";
import PhoneNumber from "awesome-phonenumber";
import base64 from "base-64";
import utf8 from "utf8";

import ShowError from "./ShowError";
import _ from "lodash";
import * as Sentry from "@sentry/nextjs";
import { childFormComponent } from "./types";
import { Props } from "../propTypes";
import { ObjectLiteral } from "../../constants";
import { User } from "../../store/main/types";
import { ProgressIndicator } from "./ProgressIndicator";
import { ResetForm } from "./ResetForm";

type State = {
    updateInfo: undefined | boolean;
    errorText: string;
    errorCount: number;
    responseRaw: ObjectLiteral | null;
};

class UpdateDataConfirmation extends Component<
    Props & childFormComponent & ReactCookieProps,
    State
> {
    state = {
        updateInfo: undefined,
        errorText: "",
        errorCount: 0,
        responseRaw: this.props.eGroups.eGroupLeaderContactEditResponse,
    };

    componentDidUpdate() {
        if (
            !_.isEqual(
                this.props.eGroups.eGroupLeaderContactEditResponse,
                this.state.responseRaw
            )
        ) {
            this.setState({
                responseRaw: this.props.eGroups.eGroupLeaderContactEditResponse,
            });

            if (
                this.props.eGroups.eGroupLeaderContactEditResponse &&
                !this.props.eGroups.eGroupLeaderContactEditSuccess
            ) {
                let errorText =
                    "There was an error saving the information. Please try again.";
                if (
                    this.props.eGroups.eGroupLeaderContactEditResponse
                        .message &&
                    _.isArray(
                        this.props.eGroups.eGroupLeaderContactEditResponse
                            .message
                    ) &&
                    this.props.eGroups.eGroupLeaderContactEditResponse
                        .message[0] &&
                    _.isObject(
                        this.props.eGroups.eGroupLeaderContactEditResponse
                            .message[0]
                    )
                ) {
                    errorText =
                        this.props.eGroups.eGroupLeaderContactEditResponse
                            .message &&
                        this.props.eGroups.eGroupLeaderContactEditResponse
                            .message &&
                        (
                            this.props.eGroups.eGroupLeaderContactEditResponse
                                .message as ObjectLiteral[]
                        )[0][
                            Object.keys(
                                this.props.eGroups
                                    .eGroupLeaderContactEditResponse.message[0]
                            )[0]
                        ];
                }

                const extraErrorData = {
                    status_code:
                        this.props.eGroups.eGroupLeaderContactEditResponse
                            .status_code,
                    other_validation_data:
                        this.props.eGroups.eGroupLeaderContactEditResponse,
                };

                if (
                    this.props.eGroups.eGroupLeaderContactEditResponse
                        .origin !== "USER"
                ) {
                    this.props.logError &&
                        this.props.logError(
                            errorText,
                            Sentry.Severity.Fatal,
                            extraErrorData
                        );
                }

                this.setState({
                    errorText: errorText,
                    errorCount: this.state.errorCount + 1,
                    responseRaw:
                        this.props.eGroups.eGroupLeaderContactEditResponse,
                });
            }

            if (
                this.props.eGroups.eGroupLeaderContactEditResponse &&
                this.props.eGroups.eGroupLeaderContactEditSuccess
            ) {
                const newData: User = Object.assign(
                    {},
                    this.props.main.userData
                );

                const {
                    emailConfirmation,
                    phoneConfirmation,
                    phoneConfirmationFormat,
                } = this.props.values;

                if (emailConfirmation) {
                    newData.email = emailConfirmation;
                } else if (
                    phoneConfirmation &&
                    this.props.main.userData &&
                    this.props.main.userData.mobileNumber !== phoneConfirmation
                ) {
                    newData.mobileNumber = phoneConfirmation.replace(
                        phoneConfirmationFormat.dialCode,
                        ""
                    );
                }

                this.setState({
                    errorText: "",
                    responseRaw:
                        this.props.eGroups.eGroupLeaderContactEditResponse,
                });

                this.props.actions.main.main_update_contact_data(newData);

                const { cookies } = this.props;
                let confirmationCodeCookieMaxAge = 1800; // 30 minutes
                let content = "";

                if (cookies) {
                    cookies.get(
                        "confirmation_" +
                            this.props.registrationType.toLowerCase()
                    );
                }

                let contentSplit: Array<any> | null = null;

                if (content && this.props.saveSession) {
                    const contentDecode = base64.decode(content);
                    contentSplit = contentDecode.split("+++");
                }

                if (content && contentSplit && contentSplit.length === 2) {
                    if (contentSplit[0]) {
                        const text = `${contentSplit[0]}+++${JSON.stringify(
                            newData
                        )}`;

                        const bytes = utf8.encode(text);
                        const contentToSave = base64.encode(bytes);
                        if (this.props.saveSession && cookies) {
                            cookies.set(
                                "confirmation_" +
                                    this.props.registrationType.toLowerCase(),
                                contentToSave,
                                {
                                    path: "/",
                                    maxAge: confirmationCodeCookieMaxAge,
                                }
                            );
                        }
                    }
                }

                this.props.nextStep(16);
            }
        }
    }

    onChangeUpdateInfo = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({
            updateInfo: event.target.value === "true",
        });
    };

    saveAndContinue = () => {
        if (this.state.updateInfo) {
            const {
                emailConfirmation,
                phoneConfirmation,
                phoneConfirmationFormat,
            } = this.props.values;

            if (emailConfirmation) {
                this.props.actions.eGroups.egroups_edit_leader_contact({
                    contactId: (this.props.main.userData &&
                        this.props.main.userData.contactId) as string,
                    email: emailConfirmation,
                });
            } else if (
                phoneConfirmation &&
                this.props.main.userData &&
                this.props.main.userData.mobileNumber !== phoneConfirmation
            ) {
                const pn = new PhoneNumber(
                    phoneConfirmation,
                    phoneConfirmationFormat.countryCode
                );

                this.props.actions.eGroups.egroups_edit_leader_contact({
                    contactId: this.props.main.userData.contactId,
                    email: "",
                    dialingCode: pn.getCountryCode(),
                    phone:
                        phoneConfirmation.replace(
                            phoneConfirmationFormat.dialCode,
                            ""
                        ) || "",
                });
            }
        } else {
            this.props.nextStep(16);
        }
    };

    render() {
        if (!this.props.main.userData) {
            return (
                <>
                    <ShowError />
                    <ResetForm
                        idPrefix={`${this.props.tagPrefixId}-update-info-error`}
                        cancelUrl={this.props.cancelUrl || ""}
                    />
                </>
            );
        }

        const mobileNumber = this.props.main.userData?.mobileNumber;
        const mailingAddress = this.props.main.userData?.mailingAddress;

        let newPhoneToShow = "";
        let oldPhoneToShow = "";

        const {
            emailConfirmation,
            phoneConfirmation,
            phoneConfirmationFormat,
        } = this.props.values;

        if (
            phoneConfirmation &&
            this.props.main.userData?.mobileNumber !== phoneConfirmation
        ) {
            const pn = new PhoneNumber(
                phoneConfirmation,
                phoneConfirmationFormat.countryCode
            );

            if (phoneConfirmationFormat.countryCode === "us") {
                newPhoneToShow = `+${pn.getCountryCode()} ${pn.getNumber(
                    "national"
                )}`;
            } else {
                newPhoneToShow = pn.getNumber("international");
            }
        }

        if (mailingAddress && mailingAddress.countryCode) {
            const pn = new PhoneNumber(
                mobileNumber as string,
                mailingAddress.countryCode
            );

            if (mailingAddress.countryCode === "US") {
                oldPhoneToShow = `+${pn.getCountryCode()} ${pn.getNumber(
                    "national"
                )}`;
            } else {
                oldPhoneToShow = pn.getNumber("international");
            }
        } else {
            oldPhoneToShow = mobileNumber as string;
        }

        let classNameOptionYes =
            "field eteam-date-select flex-two-column increase-gap p-x-spacer-lg no-border ec-border-radius text-alt-dark-grey m-b-spacer-sm full-width rel-pos ec-radio";
        let phoneClassNameOptionYes =
            "prim-font font-size-12 one-quarter-line-height regular";
        let optionClassNameOptionYes = "ec-text-dark one-quarter-line-height";
        let classNameOptionNo =
            "field eteam-date-select flex-two-column increase-gap p-x-spacer-lg no-border ec-border-radius text-alt-dark-grey m-b-spacer-sm full-width rel-pos ec-radio";
        let phoneClassNameOptionNo =
            "prim-font font-size-12 one-quarter-line-height regular";
        let optionClassNameOptionNo = "ec-text-dark one-quarter-line-height";

        if (
            this.state.updateInfo !== true &&
            this.state.updateInfo !== undefined
        ) {
            optionClassNameOptionYes = "off-selection";
            classNameOptionYes += " off-selected-box";
            phoneClassNameOptionYes += " off-selection";
            classNameOptionNo += " selected-box";
            phoneClassNameOptionNo += " ec-text-dark";
        } else if (this.state.updateInfo) {
            classNameOptionYes += " selected-box";
            phoneClassNameOptionYes += " ec-text-dark";
            optionClassNameOptionNo = "off-selection";
            classNameOptionNo += " off-selected-box";
            phoneClassNameOptionNo += " off-selection";
        } else if (this.state.updateInfo === undefined) {
            classNameOptionYes += " off-selected-box";
            classNameOptionNo += " off-selected-box";
        }

        return (
            <>
                <div className="col-xs-12 max-w-500 center-content m-b-spacer-3xl full-width update-data-step">
                    <h2 className="text-dark text-left no-bottom-margin text-center">
                        Update your Contact info
                    </h2>
                    <p className="text-dark half-here text-center">
                        We&#39;ve noticed your info doesn&#39;t match our
                        records.
                    </p>
                    <Form className={`p-y-spacer-xl `}>
                        <Form.Field>
                            <small className="m-b-spacer-md d-block">
                                Do you want to update your{" "}
                                {newPhoneToShow ? "phone number" : "email"}?
                            </small>

                            <Form.Field
                                label={
                                    <label
                                        className="d-block no-top-margin no-bottom-margin"
                                        htmlFor="Yes"
                                    >
                                        <div
                                            className={phoneClassNameOptionYes}
                                        >
                                            Yes, update my{" "}
                                            {newPhoneToShow
                                                ? "phone number"
                                                : "email"}{" "}
                                            to:
                                        </div>
                                        <div
                                            className={optionClassNameOptionYes}
                                        >
                                            {newPhoneToShow ||
                                                emailConfirmation}{" "}
                                        </div>
                                    </label>
                                }
                                value={true}
                                checked={this.state.updateInfo === true}
                                control="input"
                                type="radio"
                                id="Yes"
                                name="updateInfo"
                                className={
                                    this.state.updateInfo === true
                                        ? `${classNameOptionYes} checked`
                                        : classNameOptionYes
                                }
                                onChange={this.onChangeUpdateInfo}
                            />

                            <Form.Field
                                label={
                                    <label
                                        className="d-block no-top-margin no-bottom-margin"
                                        htmlFor="No"
                                    >
                                        <div className={phoneClassNameOptionNo}>
                                            No, keep{" "}
                                            {newPhoneToShow
                                                ? "phone number"
                                                : "email"}{" "}
                                            as:{" "}
                                        </div>
                                        <div
                                            className={optionClassNameOptionNo}
                                        >
                                            {newPhoneToShow
                                                ? oldPhoneToShow
                                                : this.props.main.userData
                                                      ?.email}{" "}
                                        </div>
                                    </label>
                                }
                                value={false}
                                checked={this.state.updateInfo === false}
                                control="input"
                                type="radio"
                                id="No"
                                name="meetGreetDate"
                                className={
                                    this.state.updateInfo === false
                                        ? `${classNameOptionNo} checked`
                                        : classNameOptionNo
                                }
                                onChange={this.onChangeUpdateInfo}
                            />
                        </Form.Field>

                        {this.state.errorText && !this.props.isLoading ? (
                            <div
                                className={`${
                                    this.state.errorText ? "ec-error" : ""
                                }`}
                            >
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.errorText}{" "}
                                </small>
                                {this.state.errorCount > 1 ? (
                                    <>
                                        <small className="ec-primary-font ec-line-height">
                                            Or
                                        </small>
                                        <Button
                                            onClick={() => {
                                                this.props.nextStep(16);
                                            }}
                                            className="button button ec-no-background no-border text-red font-size-12 text-underline"
                                        >
                                            Continue without updating
                                        </Button>
                                    </>
                                ) : null}
                            </div>
                        ) : null}

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                className="full-width no-border m-t-spacer-md text-white p-y-spacer-md ec-rou uppercase let-sp-reg ec-border-radius-sm "
                                disabled={this.state.updateInfo === undefined}
                                onClick={this.saveAndContinue}
                                id={`${this.props.tagPrefixId}-update-info-next`}
                            >
                                Next
                            </Button>
                        )}
                    </Form>
                    <ProgressIndicator currentStep={8} />
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-update-info`}
                    cancelUrl={this.props.cancelUrl || ""}
                />
            </>
        );
    }
}

export default withCookies(UpdateDataConfirmation);
